import { useCallback, useEffect, useState, useRef } from 'react';
import type { ProjectSettings } from '@gc/shared';
import { ApiError, apiRequest } from '../../api/client.js';
import { actualizarProyecto } from '../../api/projects.js';

export type PanelProduccionProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  readonly reviewedRevision: number | null;
  readonly settings: ProjectSettings;
  /**
   * Resumen de lo que afecta al Reviewer sin cambiar la revisión (assets de
   * imagen y voz asignados por el worker). Cambia → se vuelve a consultar.
   */
  readonly firma?: string;
  /** En vídeo avanzado la estructura la pone el storyboard: aquí solo se aprueba. */
  readonly soloAprobacion?: boolean;
  onCambio(): void;
};

type ReviewIssue = {
  readonly code: string;
  readonly sceneId?: string | null;
  readonly position?: number | null;
  readonly message: string;
  readonly blocking: boolean;
};

type Revision = {
  readonly issues: ReviewIssue[];
  readonly approvable: boolean;
  readonly totalDurationMs: number;
  readonly revision: number;
  readonly reviewedRevision: number | null;
};

type Produccion = {
  readonly run: { id: string; status: string; step: string; revision: number } | null;
  readonly active: boolean;
  readonly generating: boolean;
  readonly capabilities: {
    readonly capability: string;
    readonly available: boolean;
    readonly isMock: boolean;
    readonly detail: string | null;
  }[];
};

const PASOS: Record<string, string> = {
  plan: 'Estructura de escenas',
  images: 'Imágenes',
  music: 'Música',
  voice: 'Narración',
  review: 'Listo para revisar',
};

export function PanelProduccion({
  projectId,
  revision,
  status,
  reviewedRevision,
  settings,
  firma,
  soloAprobacion = false,
  onCambio,
}: PanelProduccionProps): React.JSX.Element {
  const [produccion, setProduccion] = useState<Produccion | null>(null);
  const [revisionEstado, setRevisionEstado] = useState<Revision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const recargar = useCallback(async () => {
    try {
      const [prod, rev] = await Promise.all([
        apiRequest<Produccion>(`/projects/${projectId}/production`),
        apiRequest<Revision>(`/projects/${projectId}/review`),
      ]);
      setProduccion(prod);
      setRevisionEstado(rev);
      setError(null);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo consultar la producción.');
    }
  }, [projectId]);

  // También al cambiar la revisión o el estado: una edición hecha en otro panel
  // (escenas, música) cambia lo que el Reviewer opina, y sin volver a
  // consultarlo el botón de aprobar se quedaba con la respuesta antigua.
  useEffect(() => {
    void recargar();
  }, [recargar, revision, status, firma]);

  // Mientras hay una ejecución activa se refresca; al terminar, se detiene.
  useEffect(() => {
    if (produccion?.generating !== true) return;
    const intervalo = setInterval(() => void recargar(), 3_000);
    return () => clearInterval(intervalo);
  }, [produccion?.generating, recargar]);

  // El proyecto lo carga la página, no este panel. Si aquí ya consta que la
  // generación terminó y la página sigue mostrando «Generando», se le pide que
  // recargue: sin esto las escenas no aparecían hasta refrescar a mano. Las
  // dependencias no cambian si la recarga devuelve lo mismo, así que no hay
  // bucle.
  const generando = produccion?.generating;
  // Por referencia: la página pasa una función nueva en cada render y, como
  // dependencia, relanzaría el efecto sin que nada hubiera cambiado.
  const avisar = useRef(onCambio);
  avisar.current = onCambio;
  useEffect(() => {
    if (generando === false && status === 'GENERATING') avisar.current();
  }, [generando, status]);

  async function accion(
    ruta: string,
    cuerpo?: Record<string, unknown>,
  ): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await apiRequest(ruta, { method: 'POST', ...(cuerpo === undefined ? {} : { body: cuerpo }) });
      await recargar();
      onCambio();
    } catch (causa) {
      if (causa instanceof ApiError) {
        // Un 422 de revisión trae los problemas concretos: se muestran abajo.
        setError(causa.message);
        if (causa.code === 'CONFLICT' || causa.code === 'REVIEW_REQUIRED') await recargar();
      } else {
        setError('La operación falló.');
      }
    } finally {
      setOcupado(false);
    }
  }

  // «Generando» es lo que importa aquí: un run esperando revisión no trabaja.
  const activo = produccion?.generating === true;
  const aprobado = reviewedRevision === revision;
  const puedeGenerar = !activo && ['DRAFT', 'REVIEW', 'ERROR'].includes(status);
  const puedeAprobar = !activo && status === 'REVIEW' && revisionEstado?.approvable === true;

  const bloqueantes = revisionEstado?.issues.filter((problema) => problema.blocking) ?? [];
  const avisos = revisionEstado?.issues.filter((problema) => !problema.blocking) ?? [];

  const faltanCapacidades =
    produccion?.capabilities.filter((capacidad) => !capacidad.available) ?? [];
  const conMock = produccion?.capabilities.filter((capacidad) => capacidad.isMock) ?? [];

  return (
    <section style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}>
      <h2 style={{ marginTop: 0 }}>Producción</h2>

      {/* Formato de salida. Cambiarlo invalida la aprobación; las imágenes ya
          generadas se recortan al nuevo encuadre en el montaje, así que lo
          habitual es regenerarlas después. */}
      <fieldset style={{ margin: '0 0 1rem' }}>
        <legend>Formato del vídeo</legend>
        {(['16:9', '9:16'] as const).map((valor) => (
          <label key={valor} style={{ marginRight: '1.25rem' }}>
            <input
              type="radio"
              name={`formato-${projectId}`}
              value={valor}
              checked={settings.aspectRatio === valor}
              disabled={ocupado || activo || status === 'RENDERING'}
              onChange={() => {
                if (valor === settings.aspectRatio) return;
                setOcupado(true);
                setError(null);
                void actualizarProyecto(projectId, { expectedRevision: revision, settings: { aspectRatio: valor } })
                  .then(() => onCambio())
                  .catch((causa: unknown) => setError(causa instanceof ApiError ? causa.message : 'No se pudo cambiar el formato.'))
                  .finally(() => setOcupado(false));
              }}
            />{' '}
            {valor === '16:9' ? 'Horizontal 16:9 (YouTube, 1920×1080)' : 'Vertical 9:16 (redes sociales, 1080×1920)'}
          </label>
        ))}
        <br />
        <small>
          Las imágenes ya generadas se recortan al nuevo encuadre; para aprovechar todo el formato, vuelve a
          generarlas (botón «Regenerar imagen» de cada escena o «Volver a generar lo que falte» tras quitarlas).
        </small>
      </fieldset>

      {error !== null && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      )}

      {activo && produccion?.run !== null && (
        <p role="status">
          Generando: <strong>{PASOS[produccion!.run!.step] ?? produccion!.run!.step}</strong>
        </p>
      )}

      {aprobado && !activo && (
        <p>
          Revisión <strong>{revision}</strong> aprobada. El proyecto está listo para renderizar.
        </p>
      )}

      <p style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {!soloAprobacion && (
          <button
            type="button"
            onClick={() => void accion(`/projects/${projectId}/generate`, { expectedRevision: revision })}
            disabled={ocupado || !puedeGenerar}
          >
            {status === 'DRAFT' ? 'Generar estructura y assets' : 'Volver a generar lo que falte'}
          </button>
        )}

        {activo && !soloAprobacion && (
          <button
            type="button"
            onClick={() => void accion(`/projects/${projectId}/generate/cancel`)}
            disabled={ocupado}
          >
            Cancelar generación
          </button>
        )}

        <button
          type="button"
          onClick={() => void accion(`/projects/${projectId}/approve`, { expectedRevision: revision })}
          disabled={ocupado || !puedeAprobar}
          title={
            puedeAprobar
              ? 'Aprobar esta revisión para poder renderizar'
              : 'Resuelve los problemas antes de aprobar'
          }
        >
          Aprobar revisión {revision}
        </button>
      </p>

      {revisionEstado !== null && (
        <p>
          Duración del montaje: <strong>{Math.round(revisionEstado.totalDurationMs / 1000)} s</strong>
        </p>
      )}

      {bloqueantes.length > 0 && (
        <>
          <h3>Impide aprobar</h3>
          <ul>
            {bloqueantes.map((problema, indice) => (
              <li key={`${problema.code}-${indice}`} style={{ color: 'var(--error)' }}>
                {problema.position !== null && problema.position !== undefined && (
                  <strong>Escena {problema.position + 1}: </strong>
                )}
                {problema.message}
              </li>
            ))}
          </ul>
        </>
      )}

      {avisos.length > 0 && (
        <>
          <h3>Avisos</h3>
          <ul>
            {avisos.map((problema, indice) => (
              <li key={`${problema.code}-${indice}`}>{problema.message}</li>
            ))}
          </ul>
        </>
      )}

      {faltanCapacidades.length > 0 && (
        <p>
          <small>
            Capacidades sin configurar:{' '}
            {faltanCapacidades.map((capacidad) => capacidad.capability).join(', ')}. Los pasos que
            dependen de ellas quedan pendientes; revisa <a href="/ajustes">Ajustes de IA</a>.
          </small>
        </p>
      )}

      {conMock.length > 0 && (
        <p role="note" style={{ border: '1px solid var(--aviso)', padding: '0.5rem' }}>
          <strong>Atención:</strong> {conMock.map((c) => c.capability).join(', ')} están atendidas por
          el proveedor <strong>MOCK</strong>. Los archivos generados son fixtures de desarrollo y no
          acreditan una integración real.
        </p>
      )}
    </section>
  );
}
